/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { useAuth } from 'Contexts/Auth';
import { Link } from 'react-router-dom';
import umatiLogo from '../../assets/umati_logo.png';
import styles from './Home.module.css';

type RoleKey = 'SuperAdmin' | 'Admin' | 'CompanyMember';

type RoleContent = {
  title: string;
  summary: string;
  rights: string[];
};

const ROLE_CONTENT: Record<RoleKey, RoleContent> = {
  SuperAdmin: {
    title: 'Super Admin',
    summary: 'You have full access to all companies.',
    rights: [
      'Add companies and manage their names',
      'Manage machines, clients, users and software of every company',
      'Manage fairs',
    ],
  },
  Admin: {
    title: 'Admin',
    summary: 'You manage the companies you are assigned to.',
    rights: [
      'Manage the machines and clients of your companies',
      'Create users and assign them to your companies',
      'Manage the software entries of your companies',
    ],
  },
  CompanyMember: {
    title: 'Company Member',
    summary: 'You can view the data of the companies you are assigned to.',
    rights: ['View the machines and software of your companies', 'Change your own password'],
  },
};

const getRoleKey = (roles: string[]): RoleKey => {
  if (roles.includes('SuperAdmin')) return 'SuperAdmin';
  if (roles.includes('Admin')) return 'Admin';
  return 'CompanyMember';
};

const Home = () => {
  const { user } = useAuth();
  const role = ROLE_CONTENT[getRoleKey(user?.Roles ?? [])];

  return (
    <div className={styles['admin-home']}>
      <div className={styles['admin-title']}>
        <img src={umatiLogo} alt="umati" className={styles['admin-title-logo']} />
        <span className={styles['admin-title-context']}>ADMIN</span>
      </div>

      <h2 className={styles.greeting}>{user?.Username ? `Welcome, ${user.Username}` : 'Welcome'}</h2>

      <p className={styles['admin-intro']}>
        This is the administration area of umati for machines, companies, users and software.
      </p>

      <section className={styles['role-card']} aria-label="Your role">
        <span className={styles['role-label']}>Your role</span>
        <h3>{role.title}</h3>
        <p className={styles['role-summary']}>{role.summary}</p>
        <ul>
          {role.rights.map((right) => (
            <li key={right}>{right}</li>
          ))}
        </ul>
      </section>

      <p className={styles['admin-hint']}>
        Your account details and password are under <Link to="/new/Me">My Account</Link>.
      </p>
    </div>
  );
};

export default Home;
